import React, { useState, useEffect, useRef } from 'react';
import { WidgetSettings } from '../types';
import { Palette, Settings, Copy, Download, Monitor, Smartphone } from 'lucide-react';

interface CustomizerPanelProps {
  settings: WidgetSettings;
  onSettingsChange: (settings: WidgetSettings) => void;
  onDeviceChange: (device: 'desktop' | 'mobile') => void;
}

export const CustomizerPanel: React.FC<CustomizerPanelProps> = ({
  settings,
  onSettingsChange,
  onDeviceChange
}) => {
  const [activeTab, setActiveTab] = useState<'common' | 'desktop' | 'mobile'>('common');
  const isInitializedRef = useRef(false);
  
  const updateSettings = (updates: Partial<WidgetSettings>) => {
    onSettingsChange({ ...settings, ...updates });
  };

  const handleTabChange = (tab: 'common' | 'desktop' | 'mobile') => {
    // タブ切り替え
    setActiveTab(tab);
    // デスクトップ・モバイルタブの場合はプレビューも連動
    if (tab === 'desktop') {
      // デスクトッププレビューに切り替え
      onDeviceChange('desktop');
    } else if (tab === 'mobile') {
      // モバイルプレビューに切り替え
      onDeviceChange('mobile');
    }
    // 共通設定の場合は現在のプレビューを維持（何もしない）
  };

  // 初期化時のみデスクトッププレビューを設定
  useEffect(() => {
    if (!isInitializedRef.current) {
      // カスタマイザー初期化: デスクトップ設定
      onDeviceChange('desktop');
      isInitializedRef.current = true;
    }
  }, [onDeviceChange]);

  return (
    <div className="w-96 bg-white border-r border-gray-200 h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">設定</h2>
          <button
            onClick={() => {
              const iframe = document.querySelector('iframe');
              if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage({ type: 'resetState' }, '*');
              }
              window.location.reload();
            }}
            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
          >
            リセット
          </button>
        </div>

        {/* タブ切り替え */}
        <div className="bg-gray-50 p-1 rounded-lg mb-4">
          <div className="flex space-x-1">
            <button
              onClick={() => handleTabChange('common')}
              className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
                activeTab === 'common' 
                  ? 'bg-white text-blue-600 shadow-sm border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Palette className="w-3 h-3 mr-1 inline" />
              共通設定
            </button>
            <button
              onClick={() => handleTabChange('desktop')}
              className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
                activeTab === 'desktop' 
                  ? 'bg-white text-blue-600 shadow-sm border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Monitor className="w-3 h-3 mr-1 inline" />
              デスクトップ
            </button>
            <button
              onClick={() => handleTabChange('mobile')}
              className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
                activeTab === 'mobile' 
                  ? 'bg-white text-blue-600 shadow-sm border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Smartphone className="w-3 h-3 mr-1 inline" />
              モバイル
            </button>
          </div>
        </div>

        {/* 共通設定 */}
        {activeTab === 'common' && (
          <div className="space-y-6">
            {/* カラー設定 */}
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-900 mb-3">
                <Palette className="w-4 h-4 mr-2" />
                カラー設定
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    プライマリカラー
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) => updateSettings({ primaryColor: e.target.value })}
                      className="w-8 h-8 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={settings.primaryColor}
                      onChange={(e) => updateSettings({ primaryColor: e.target.value })}
                      className="input-field text-xs"
                      placeholder="#3182ce"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    セカンダリカラー
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={settings.secondaryColor}
                      onChange={(e) => updateSettings({ secondaryColor: e.target.value })}
                      className="w-8 h-8 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={settings.secondaryColor}
                      onChange={(e) => updateSettings({ secondaryColor: e.target.value })}
                      className="input-field text-xs"
                      placeholder="#f5f5f5"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* チャットウィンドウサイズ */}
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-900 mb-3">
                <Settings className="w-4 h-4 mr-2" />
                チャットウィンドウサイズ
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    幅 (300-600px)
                  </label>
                  <input
                    type="range"
                    min="300"
                    max="600"
                    value={settings.width}
                    onChange={(e) => updateSettings({ width: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.width}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    高さ (400-800px)
                  </label>
                  <input
                    type="range"
                    min="400"
                    max="800"
                    value={settings.height}
                    onChange={(e) => updateSettings({ height: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.height}px</span>
                </div>
              </div>
            </div>

            {/* フォント設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">フォント設定</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    ヘッダーフォントサイズ (10-24px)
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="24"
                    value={settings.headerFontSize}
                    onChange={(e) => updateSettings({ headerFontSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.headerFontSize}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    メッセージフォントサイズ (10-20px)
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="20"
                    value={settings.messageFontSize}
                    onChange={(e) => updateSettings({ messageFontSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.messageFontSize}px</span>
                </div>
              </div>
            </div>

            {/* レイアウト設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">レイアウト設定</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    ヘッダーの高さ (60-100px)
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="100"
                    value={settings.headerHeight}
                    onChange={(e) => updateSettings({ headerHeight: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.headerHeight}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    フッター（入力欄）の高さ (60-100px)
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="100"
                    value={settings.footerHeight}
                    onChange={(e) => updateSettings({ footerHeight: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.footerHeight}px</span>
                </div>
              </div>
            </div>

            {/* アイコンサイズ設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">アイコンサイズ設定</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    アバターアイコンサイズ (16-128px)
                  </label>
                  <input
                    type="range"
                    min="16"
                    max="128"
                    value={settings.avatarIconSize}
                    onChange={(e) => updateSettings({ avatarIconSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.avatarIconSize}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    UIアイコンサイズ (12-64px)
                  </label>
                  <input
                    type="range"
                    min="12"
                    max="64"
                    value={settings.uiIconSize}
                    onChange={(e) => updateSettings({ uiIconSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.uiIconSize}px</span>
                </div>
              </div>
            </div>

            {/* テキスト設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">テキスト設定</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    ヘッダーテキスト
                  </label>
                  <input
                    type="text"
                    value={settings.headerText || ''}
                    onChange={(e) => updateSettings({ headerText: e.target.value })}
                    className="input-field text-xs"
                    placeholder="チャットサポート"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    初期メッセージ
                  </label>
                  <textarea
                    value={settings.initialMessage || ''}
                    onChange={(e) => updateSettings({ initialMessage: e.target.value })}
                    className="input-field text-xs"
                    rows={3}
                    placeholder="こんにちは！何かお手伝いできることはありますか？"
                  />
                </div>
              </div>
            </div>


            {/* アイコン設定 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">アイコン設定</h3>
              
              {/* バブルアイコン */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-3">バブルアイコン</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['bubble1', 'bubble2', 'bubble3', 'bubble4'].map((iconType) => (
                    <button
                      key={iconType}
                      onClick={() => updateSettings({ bubbleIcon: iconType as 'bubble1' | 'bubble2' | 'bubble3' | 'bubble4' })}
                      className={`flex items-center justify-center p-4 rounded-lg border-2 aspect-square ${
                        settings.bubbleIcon === iconType
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <img 
                        src={`/icons/${iconType}-black.svg`} 
                        alt={`アイコン ${iconType}`}
                        className="w-8 h-8"
                      />
                    </button>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">カスタムURL</label>
                  <input
                    type="text"
                    value={settings.bubbleIconUrl || ''}
                    onChange={(e) => updateSettings({ bubbleIconUrl: e.target.value, bubbleIcon: 'custom' })}
                    className="input-field text-xs"
                    placeholder="https://example.com/icon.svg"
                  />
                </div>
              </div>

              {/* ユーザーアイコン */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-3">ユーザーアイコン</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['user1', 'user2', 'user3', 'user4'].map((iconNum) => (
                    <button
                      key={iconNum}
                      onClick={() => updateSettings({ userIcon: iconNum as 'user1' | 'user2' | 'user3' | 'user4' })}
                      className={`flex items-center justify-center p-4 rounded-lg border-2 aspect-square ${
                        settings.userIcon === iconNum
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <img 
                        src={`/icons/${iconNum}.svg`} 
                        alt={`ユーザーアイコン ${iconNum}`}
                        className="w-8 h-8"
                      />
                    </button>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">カスタムURL</label>
                  <input
                    type="text"
                    value={settings.userIconUrl || ''}
                    onChange={(e) => updateSettings({ userIconUrl: e.target.value, userIcon: 'custom' })}
                    className="input-field text-xs"
                    placeholder="https://example.com/user.svg"
                  />
                </div>
              </div>

              {/* ボットアイコン */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-3">ボットアイコン</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['bot1', 'bot2', 'bot3', 'bot4'].map((iconNum) => (
                    <button
                      key={iconNum}
                      onClick={() => updateSettings({ botIcon: iconNum as 'bot1' | 'bot2' | 'bot3' | 'bot4' })}
                      className={`flex items-center justify-center p-4 rounded-lg border-2 aspect-square ${
                        settings.botIcon === iconNum
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <img 
                        src={`/icons/${iconNum}-black.svg`} 
                        alt={`ボットアイコン ${iconNum}`}
                        className="w-8 h-8"
                      />
                    </button>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">カスタムURL</label>
                  <input
                    type="text"
                    value={settings.botIconUrl || ''}
                    onChange={(e) => updateSettings({ botIconUrl: e.target.value, botIcon: 'custom' })}
                    className="input-field text-xs"
                    placeholder="https://example.com/bot.svg"
                  />
                </div>
              </div>

              {/* 送信アイコン */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-3">送信アイコン</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['send1', 'send2', 'send3', 'send4'].map((iconNum) => (
                    <button
                      key={iconNum}
                      onClick={() => updateSettings({ sendIcon: iconNum as 'send1' | 'send2' | 'send3' | 'send4' })}
                      className={`flex items-center justify-center p-4 rounded-lg border-2 aspect-square ${
                        settings.sendIcon === iconNum
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <img 
                        src={`/icons/${iconNum}-black.svg`} 
                        alt={`送信アイコン ${iconNum}`}
                        className="w-8 h-8"
                      />
                    </button>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">カスタムURL</label>
                  <input
                    type="text"
                    value={settings.sendIconUrl || ''}
                    onChange={(e) => updateSettings({ sendIconUrl: e.target.value, sendIcon: 'custom' })}
                    className="input-field text-xs"
                    placeholder="https://example.com/send.svg"
                  />
                </div>
              </div>

              {/* 閉じるアイコン */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-3">閉じるアイコン</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {['close1', 'close2', 'close3', 'close4'].map((iconNum) => (
                    <button
                      key={iconNum}
                      onClick={() => updateSettings({ closeIcon: iconNum as 'close1' | 'close2' | 'close3' | 'close4' })}
                      className={`flex items-center justify-center p-4 rounded-lg border-2 aspect-square ${
                        settings.closeIcon === iconNum
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <img 
                        src={`/icons/${iconNum}-black.svg`} 
                        alt={`閉じるアイコン ${iconNum}`}
                        className="w-8 h-8"
                      />
                    </button>
                  ))}
                </div>
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">カスタムURL</label>
                  <input
                    type="text"
                    value={settings.closeIconUrl || ''}
                    onChange={(e) => updateSettings({ closeIconUrl: e.target.value, closeIcon: 'custom' })}
                    className="input-field text-xs"
                    placeholder="https://example.com/close.svg"
                  />
                </div>
              </div>
            </div>

            {/* バブル形状 */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">バブル形状</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => updateSettings({ bubbleShape: 'circle' })}
                  className={`flex-1 px-3 py-2 text-xs rounded-md border ${
                    settings.bubbleShape === 'circle'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  円形
                </button>
                <button
                  onClick={() => updateSettings({ bubbleShape: 'square' })}
                  className={`flex-1 px-3 py-2 text-xs rounded-md border ${
                    settings.bubbleShape === 'square'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  角丸四角
                </button>
              </div>
            </div>
          </div>
        )}

        {/* デスクトップ設定 */}
        {activeTab === 'desktop' && (
          <div className="space-y-6">
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-900 mb-3">
                <Monitor className="w-4 h-4 mr-2" />
                デスクトップ設定
              </h3>
              
              {/* バブル位置選択 */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  バブル位置
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => updateSettings({ desktopPosition: 'top-left' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.desktopPosition === 'top-left'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    左上
                  </button>
                  <button
                    onClick={() => updateSettings({ desktopPosition: 'top-right' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.desktopPosition === 'top-right'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    右上
                  </button>
                  <button
                    onClick={() => updateSettings({ desktopPosition: 'bottom-left' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.desktopPosition === 'bottom-left'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    左下
                  </button>
                  <button
                    onClick={() => updateSettings({ desktopPosition: 'bottom-right' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.desktopPosition === 'bottom-right'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    右下
                  </button>
                </div>
              </div>

              {/* バブルサイズ */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  バブルサイズ (40-400px)
                </label>
                <input
                  type="range"
                  min="40"
                  max="400"
                  value={settings.bubbleSize}
                  onChange={(e) => updateSettings({ bubbleSize: parseInt(e.target.value) })}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{settings.bubbleSize}px</span>
              </div>

              {/* 位置調整 */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    端からの距離X (0-800px)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="800"
                    value={settings.offsetX}
                    onChange={(e) => updateSettings({ offsetX: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.offsetX}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    端からの距離Y (0-800px)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="800"
                    value={settings.offsetY}
                    onChange={(e) => updateSettings({ offsetY: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.offsetY}px</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* モバイル設定 */}
        {activeTab === 'mobile' && (
          <div className="space-y-6">
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-900 mb-3">
                <Smartphone className="w-4 h-4 mr-2" />
                モバイル設定
              </h3>
              
              {/* バブル位置選択 */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">
                  バブル位置
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => updateSettings({ mobilePosition: 'top-left' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.mobilePosition === 'top-left'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    左上
                  </button>
                  <button
                    onClick={() => updateSettings({ mobilePosition: 'top-right' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.mobilePosition === 'top-right'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    右上
                  </button>
                  <button
                    onClick={() => updateSettings({ mobilePosition: 'bottom-left' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.mobilePosition === 'bottom-left'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    左下
                  </button>
                  <button
                    onClick={() => updateSettings({ mobilePosition: 'bottom-right' })}
                    className={`flex items-center justify-center p-2 text-xs rounded-md border ${
                      settings.mobilePosition === 'bottom-right'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    右下
                  </button>
                </div>
              </div>

              {/* モバイルバブルサイズ */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  バブルサイズ (40-200px)
                </label>
                <input
                  type="range"
                  min="40"
                  max="200"
                  value={settings.mobileBubbleSize}
                  onChange={(e) => updateSettings({ mobileBubbleSize: parseInt(e.target.value) })}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{settings.mobileBubbleSize}px</span>
              </div>

              {/* 位置調整 */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    端からの距離X (0-800px)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="800"
                    value={settings.mobileOffsetX}
                    onChange={(e) => updateSettings({ mobileOffsetX: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.mobileOffsetX}px</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    端からの距離Y (0-800px)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="800"
                    value={settings.mobileOffsetY}
                    onChange={(e) => updateSettings({ mobileOffsetY: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-xs text-gray-500">{settings.mobileOffsetY}px</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 埋め込みコード */}
      <div className="border-t border-gray-200 bg-white">
        <EmbedCodeSection settings={settings} />
      </div>
    </div>
  );
};

// 埋め込みコードセクション
const EmbedCodeSection: React.FC<{ settings: WidgetSettings }> = ({ settings }) => {
  const [copySuccess, setCopySuccess] = useState<string>('');

  const generateEmbedCode = () => {
    const currentOrigin = window.location.origin;
    
    // configオブジェクトから不要なURLプロパティを削除し、
    // プリセットアイコンは名前のまま渡すように設定を整形する
    const {
      bubbleIconUrl,
      userIconUrl,
      botIconUrl,
      sendIconUrl,
      closeIconUrl,
      ...restOfSettings
    } = settings;

    const configWithUrls = {
      ...restOfSettings,
      // セキュリティ上の理由によりAPIキーは埋め込みコードから除外
      difyApiKey: undefined,

      // プリセットアイコンは名前のまま、カスタムの場合のみURLを使用
      bubbleIcon: settings.bubbleIcon === 'custom' ? bubbleIconUrl : settings.bubbleIcon,
      userIcon: settings.userIcon === 'custom' ? userIconUrl : settings.userIcon,
      botIcon: settings.botIcon === 'custom' ? botIconUrl : settings.botIcon,
      sendIcon: settings.sendIcon === 'custom' ? sendIconUrl : settings.sendIcon,
      closeIcon: settings.closeIcon === 'custom' ? closeIconUrl : settings.closeIcon,
      
      // アイコンのベースURLは必ず渡す
      iconBaseUrl: currentOrigin,
      widgetBaseUrl: currentOrigin,
      
      // APIエンドポイントも現在のオリジンを使用
      apiEndpoint: currentOrigin + '/.netlify/functions/chat'
    };
    const config = JSON.stringify(configWithUrls, null, 2);
    return `<script>
(function() {
  // チャットボットウィジェット設定
  const config = ${config};
  
  // ウィジェットスクリプトを読み込んで初期化（キャッシュ回避）
  const script = document.createElement('script');
  script.src = '${currentOrigin}/chatbot-widget.js?v=' + Date.now();
  script.onload = () => {
    if (window.ChatbotWidget) {
      window.ChatbotWidget.init(config);
    }
  };
  document.head.appendChild(script);
})();
</script>`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setCopySuccess('コピーしました！');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      setCopySuccess('コピーに失敗しました');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const downloadCode = () => {
    const embedCode = generateEmbedCode();
    const htmlContent = `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>チャットボットウィジェット テスト</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    .section {
      margin-bottom: 40px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
    }
    h1 { color: #2c3e50; margin-bottom: 20px; }
    h2 { color: #34495e; margin: 30px 0 15px 0; }
    p { margin-bottom: 15px; }
    .highlight { background-color: #fff3cd; padding: 10px; border-radius: 4px; margin: 15px 0; }
    .content-block { min-height: 300px; }
  </style>
</head>
<body>
  <h1>🤖 チャットボットウィジェット テストページ</h1>
  <div class="highlight">
    <strong>💡 テスト方法:</strong> 右下のバブルをクリックしてチャットボットを起動してください。ページをスクロールしてもバブルが追従することを確認できます。
  </div>

  <div class="section content-block">
    <h2>📋 このテストページについて</h2>
    <p>このページは、チャットボットウィジェットが正常に動作するかをテストするためのサンプルページです。</p>
    <p>ページが長いため、スクロール時にチャットボットバブルが固定表示されるかどうかを確認できます。</p>
    <p>バブルは常に画面内の同じ位置に表示され続けるはずです。</p>
  </div>

  <div class="section content-block">
    <h2>⚙️ ウィジェットの機能</h2>
    <p>• バブルアイコンをクリックするとチャット画面が開きます</p>
    <p>• チャット画面内でメッセージを送信できます</p>
    <p>• 閉じるボタンでチャット画面を閉じることができます</p>
    <p>• デスクトップとモバイルで異なる表示サイズが設定可能です</p>
    <p>• カスタムアイコンやカラーテーマが適用されます</p>
  </div>

  <div class="section content-block">
    <h2>📱 レスポンシブ対応</h2>
    <p>このウィジェットはレスポンシブ対応しており、デスクトップとモバイルで最適な表示サイズに調整されます。</p>
    <p>画面サイズを変更してテストしてみてください。</p>
    <p>モバイル表示では、より使いやすいサイズとレイアウトで表示されます。</p>
  </div>

  <div class="section content-block">
    <h2>🎨 カスタマイズ機能</h2>
    <p>ウィジェットは以下の要素をカスタマイズできます：</p>
    <p>• プライマリカラー（バブルやボタンの色）</p>
    <p>• セカンダリカラー（背景色など）</p>
    <p>• 各種アイコン（バブル、ユーザー、ボット、送信、閉じる）</p>
    <p>• 表示位置（デスクトップ・モバイル別）</p>
    <p>• サイズ設定（バブル、ウィンドウ、アイコンサイズ）</p>
  </div>

  <div class="section content-block">
    <h2>🔧 技術仕様</h2>
    <p>• 軽量なJavaScriptライブラリ</p>
    <p>• 外部依存関係なし</p>
    <p>• モダンブラウザ対応</p>
    <p>• セキュアな実装</p>
    <p>• APIエンドポイント連携可能</p>
  </div>

  <div class="section content-block">
    <h2>📊 パフォーマンス</h2>
    <p>このウィジェットは高パフォーマンスを重視して設計されています：</p>
    <p>• 非同期読み込み</p>
    <p>• 最小限のDOMツリー変更</p>
    <p>• 効率的なイベントハンドリング</p>
    <p>• メモリリーク対策</p>
  </div>

  <div class="section content-block">
    <h2>🚀 導入方法</h2>
    <p>1. 生成された埋め込みコードをコピー</p>
    <p>2. HTMLファイルの&lt;/body&gt;タグ直前に貼り付け</p>
    <p>3. 設定をカスタマイズ（必要に応じて）</p>
    <p>4. ページを公開してテスト</p>
  </div>

  <div class="section content-block">
    <h2>✅ 動作テスト項目</h2>
    <p>以下の項目をテストしてください：</p>
    <p>□ バブルが表示される</p>
    <p>□ バブルクリックでチャット画面が開く</p>
    <p>□ メッセージ送信ができる</p>
    <p>□ 閉じるボタンが機能する</p>
    <p>□ スクロール時もバブルが固定表示される</p>
    <p>□ モバイル表示で適切にリサイズされる</p>
  </div>

  <div class="section content-block">
    <h2>🎯 まとめ</h2>
    <p>チャットボットウィジェットのテストが完了しましたら、実際のウェブサイトに導入してください。</p>
    <p>問題がある場合は、設定を見直すか、カスタマイザーで調整してください。</p>
    <p>この長いページでスクロールテストも十分に行えるはずです。</p>
  </div>

  <div style="height: 100px; margin: 50px 0; text-align: center; color: #666;">
    <p>--- ページ終端 ---</p>
    <p>右下のチャットボットバブルが正常に表示されていることを確認してください。</p>
  </div>
  
  ${embedCode}
</body>
</html>`;
    const fileName = 'chatbot-widget-test.html';
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">埋め込みコード</h3>
      
      {/* アクションボタン */}
      <div className="flex space-x-2 mb-3">
        <button
          onClick={copyToClipboard}
          className="btn-primary text-xs"
        >
          <Copy className="w-3 h-3 mr-1" />
          コピー
        </button>
        <button
          onClick={downloadCode}
          className="btn-secondary text-xs"
        >
          <Download className="w-3 h-3 mr-1" />
          ダウンロード
        </button>
      </div>

      {copySuccess && (
        <div className="text-xs text-green-600 mb-2">{copySuccess}</div>
      )}

      {/* コード表示エリア */}
      <div className="bg-gray-50 rounded-md p-3 mb-3">
        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono overflow-x-auto max-h-40">
          {generateEmbedCode()}
        </pre>
      </div>

      {/* 使用方法 */}
      <div className="bg-gray-50 rounded-md p-3">
        <h4 className="text-xs font-medium text-gray-900 mb-2">使用方法</h4>
        <div className="text-xs text-gray-600 space-y-1">
          <p>• このスクリプトタグを&lt;/body&gt;タグの直前に貼り付けてください</p>
          <p>• ダウンロードボタンでテスト用HTMLファイルを取得できます</p>
        </div>
      </div>
    </div>
  );
};